import { useMemo, useCallback } from "react";
import { useStoryboard } from "./useStoryboard";
import { useSlides } from "./useSlide";
import { Slide, StoryboardSection } from "pages/media/types/";

export interface SectionWithSlides extends StoryboardSection {
  slides: Slide[];
}

export interface ScriptSpecs {
  thinking_process: string[];
  objective?: string;
  duration?: number;
  sections?: StoryboardSection[];
}

export const useStoryboardWithSlides = (
  storyboardPath?: string,
  slidesPath?: string
) => {
  const storyboard = useStoryboard({
    path: storyboardPath,
  });
  const slides = useSlides(slidesPath);

  const slidesBySection = useMemo(() => {
    if (!slides.data?.slides) return new Map<string, Slide[]>();

    return slides.data.slides.reduce((acc, slide) => {
      if (!slide.section_id) return acc;
      const existing = acc.get(slide.section_id) ?? [];
      acc.set(slide.section_id, [...existing, slide]);
      return acc;
    }, new Map<string, Slide[]>());
  }, [slides.data]);

  const sectionsWithSlides = useMemo((): SectionWithSlides[] => {
    if (!storyboard.data?.sections) return [];

    return storyboard.data.sections.map((section) => ({
      ...section,
      slides: slidesBySection.get(section.id) ?? [],
    }));
  }, [storyboard.data, slidesBySection]);

  const refetchAll = useCallback(async () => {
    await Promise.all([storyboard.refetch(), slides.refetch()]);
  }, [storyboard, slides]);

  return {
    // Mapped data
    title: storyboard.data?.title ?? null,
    sections: sectionsWithSlides,

    // Raw data
    storyboard: storyboard.data,
    slides: slides.data?.slides ?? [],

    // Status
    isLoading: storyboard.isLoading || slides.isLoading,
    isStoryboardLoading: storyboard.isLoading,
    isSlidesLoading: slides.isLoading,
    isError: storyboard.isError || slides.isError,
    isSuccess: storyboard.isSuccess && slides.isSuccess,

    // Actions
    addSlide: slides.addSlide,
    setSlide: slides.setSlide,
    setSlides: slides.setSlides,
    updateSection: storyboard.updateSection,
    reorderSections: storyboard.reorderSections,

    // Refetch
    refetchAll,
    refetchStoryboard: storyboard.refetch,
    refetchSlides: slides.refetch,
  };
};
